import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Pencil, Upload, Loader2 } from "lucide-react";
import { api, fmtINR, pkgImage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function PackagesAdmin() {
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);
  const [uploadingId, setUploadingId] = useState(null);

  const load = () => api.get("/admin/packages").then(({ data }) => setRows(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const uploadImage = async (pkgId, file) => {
    if (!file) return;
    setUploadingId(pkgId);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/admin/packages/${pkgId}/image`, fd);
      toast.success("Photo updated");
      load();
    } catch { toast.error("Upload failed"); }
    finally { setUploadingId(null); }
  };

  const save = async () => {
    try {
      await api.patch(`/admin/packages/${edit.id}`, {
        price: Number(edit.price),
        call_for_price: edit.call_for_price,
        allow_booking: edit.allow_booking,
        allow_inquiry: edit.allow_inquiry,
        customization_extra: edit.customization_extra,
        customization_charge: Number(edit.customization_charge) || 0,
        active: edit.active,
      });
      toast.success("Package updated");
      setEdit(null);
      load();
    } catch (e) { toast.error("Failed to save"); }
  };

  return (
    <div>
      <h1 className="font-display text-3xl text-zinc-900 mb-6">Packages & Pricing</h1>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {rows.map((p) => (
          <div key={p.id} className="bg-card border border-pink-200 rounded-xl overflow-hidden" data-testid={`admin-pkg-${p.slug}`}>
            <div className="relative h-28 w-full group/img">
              <img src={pkgImage(p)} alt="" className="h-28 w-full object-cover" />
              <label className="absolute inset-0 flex items-center justify-center bg-black/45 opacity-0 group-hover/img:opacity-100 transition-opacity cursor-pointer">
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  data-testid={`pkg-photo-input-${p.slug}`}
                  onChange={(e) => uploadImage(p.id, e.target.files[0])}
                />
                <span className="flex items-center gap-1.5 text-white text-sm font-medium">
                  {uploadingId === p.id ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} Change Photo
                </span>
              </label>
            </div>
            <div className="p-4">
              <div className="flex items-center justify-between">
                <h3 className="text-zinc-900 font-medium">{p.name_en}</h3>
                {!p.active && <span className="text-xs text-red-400">hidden</span>}
              </div>
              <p className="text-xs text-zinc-500">{p.name_gu}</p>
              <div className="mt-2 text-gold font-display text-lg">{p.call_for_price ? "Call for Price" : fmtINR(p.price)}</div>
              {p.customization_extra && <span className="text-[11px] text-amber-500/80">+ Customization{p.customization_charge > 0 ? ` ₹${p.customization_charge}` : " extra"}</span>}
              <Button size="sm" variant="outline" onClick={() => setEdit({ ...p })} data-testid={`edit-pkg-${p.slug}`} className="w-full mt-3 border-pink-300 text-zinc-700"><Pencil size={14} className="mr-1" /> Edit</Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="bg-card border-pink-200 text-zinc-900">
          {edit && (
            <>
              <DialogHeader><DialogTitle className="font-display text-xl">{edit.name_en}</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label className="text-zinc-700">Price (₹)</Label>
                  <Input type="number" value={edit.price} data-testid="pkg-price-input" onChange={(e) => setEdit({ ...edit, price: e.target.value })} className="mt-1 bg-white border-pink-200 text-zinc-900" />
                </div>
                <div>
                  <Label className="text-zinc-700">Customization Charge (₹)</Label>
                  <Input type="number" value={edit.customization_charge ?? 0} data-testid="pkg-custom-charge-input" onChange={(e) => setEdit({ ...edit, customization_charge: e.target.value })} className="mt-1 bg-white border-pink-200 text-zinc-900" />
                  <p className="text-[11px] text-zinc-500 mt-1">Shown as "+ Customization ₹amount" when enabled below.</p>
                </div>
                {[
                  ["call_for_price", "Call for Price"],
                  ["allow_booking", "Allow Online Booking"],
                  ["allow_inquiry", "Allow Inquiry"],
                  ["customization_extra", "Show 'Customization extra'"],
                  ["active", "Visible on website"],
                ].map(([k, label]) => (
                  <div key={k} className="flex items-center justify-between">
                    <span className="text-sm text-zinc-700">{label}</span>
                    <Switch checked={!!edit[k]} onCheckedChange={(v) => setEdit({ ...edit, [k]: v })} data-testid={`pkg-switch-${k}`} />
                  </div>
                ))}
                <Button onClick={save} data-testid="save-pkg-btn" className="w-full gold-gradient text-white font-semibold rounded-full">Save Changes</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
